import classnames from 'classnames';
import apiFetch from '@wordpress/api-fetch';

import './editor.scss';
import { InspectorTabs } from '@components/inspector-tabs';
import { getBlockClassNames } from '@components/class-dropdown';
import { DEFAULT_CSS_CLASS } from '@constants';
import { InspectorGeneral, InspectorAppearance } from './settings';
import { getInnerBlockTemplate } from '@helpers/utils.js';
import { FocusableIcon } from '@components/focusable-block-icon';
import relatedPostsIcon from './icons';
import { title } from './block.json';
import { cacheMiddleware } from '@components/api-call-cache-middleware';

const {
  i18n: { __ },
  components: { Notice },
  element: { useState, useEffect },
  blockEditor: { useBlockProps, useInnerBlocksProps, useSetting },
  data: { useDispatch, useSelect },
  blocks: { createBlock },
  url: { addQueryArgs },
} = wp;

const templateImageAppearance = [
  [
    'b3rg/div',
    {
      blockClassNames: [
        {
          value: `e-sgp__media`,
          label: 'e-sgp__media',
        },
      ],
      enableFocusableIcon: false,
    },
    [['core/post-featured-image']],
  ],
  [
    'b3rg/div',
    {
      blockClassNames: [
        {
          value: `e-sgp__content`,
          label: 'e-sgp__content',
        },
      ],
      enableFocusableIcon: false,
    },
    [
      ['core/post-title'],
      ['core/post-date'],
      ['b3rg/meta-field', { selectedMetaField: 'learn_more_label' }],
    ],
  ],
];
const templateDefault = [
  [
    'b3rg/div',
    {
      blockClassNames: [
        {
          value: `e-sgp__content`,
          label: 'e-sgp__content',
        },
      ],
      enableFocusableIcon: false,
    },
    [
      ['core/post-title'],
      ['core/post-date'],
      ['b3rg/meta-field', { selectedMetaField: 'learn_more_label' }],
    ],
  ],
];
const Edit = (props) => {
  const { attributes, clientId, name } = props;
  const {
    numberOfPosts,
    selection,
    orderBy,
    order,
    postTaxonomies,
    isBackgroundImage,
    anchorAppearance,
    blockClassNames,
    blockVisibility,
    enableFocusableIcon,
  } = attributes;

  const [defaultPostTaxonomies, setDefaultPostTaxonomies] = useState([]);

  const [postsAvailable, setPostsAvailable] = useState(selection !== 'auto');

  const [isPostLoadingInProgress, setIsPostLoadingInProgress] = useState(true);

  const { replaceInnerBlocks } = useDispatch('core/block-editor');
  const { innerBlocks, currentPostId, currentPostType } = useSelect(
    (select) => ({
      innerBlocks: select('core/block-editor').getBlocks(clientId),
      currentPostId: select('core/editor').getCurrentPostId(),
      currentPostType: select('core/editor').getCurrentPostType(),
    }),
  );
  const defaultPostTaxonomyOptions = [];
  const ALLOWED_BLOCKS = ['b3rg/single-post'];
  // Checks if the post template is extended from the theme
  const extendedTemplate = useSetting(`custom.blocks.${name}.postTemplate`);

  const createRelatedPosts = (
    res,
    innerBlockTemplates,
    isDisablePostSelect,
    relatedPostBlockInitialTemplate,
  ) => {
    const innerBlocks = [];
    res.forEach((relatedPost, key) => {
      if (
        relatedPost?.ID &&
        relatedPost?.post_title &&
        relatedPost?.post_type
      ) {
        const { ID, post_type: postType, post_title: postTitle } = relatedPost;
        //check current single-post has inner template
        let template = innerBlockTemplates[key]
          ? innerBlockTemplates[key]
          : relatedPostBlockInitialTemplate;
        // Gives priority to the extended template if defined in theme.json
        template = extendedTemplate ?? template;

        innerBlocks.push(
          createBlock('b3rg/single-post', {
            selectedPost: {
              label: postTitle,
              value: ID,
            },
            selectedPostType: postType,
            isBackgroundImage,
            anchorAppearance,
            template,
            isRelatedPost: true,
            isDisablePostSelect,
          }),
        );
      }
    });
    return innerBlocks;
  };

  const getRelatedPosts = (param) => {
    setIsPostLoadingInProgress(true);
    const { selection } = param;
    const isDisablePostSelect = selection === 'manual' ? false : true;

    const relatedPostBlockInitialTemplate = !isBackgroundImage
      ? templateImageAppearance
      : templateDefault;

    //get current each single post template using inner block
    const innerBlockTemplates = innerBlocks.length
      ? innerBlocks.map(getInnerBlockTemplate)
      : [];

    apiFetch.use(cacheMiddleware);
    apiFetch({
      path: '/api/b3rg/related-posts/get-posts',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(param),
      cachingEnabled: true,
    })
      .then((res) => {
        const innerBlocksNew =
          res.length > 0
            ? createRelatedPosts(
                res,
                innerBlockTemplates,
                isDisablePostSelect,
                relatedPostBlockInitialTemplate,
              )
            : [];
        if (innerBlocksNew.length > 0) {
          replaceInnerBlocks(clientId, innerBlocksNew, false);
          setPostsAvailable(true);
        }
        setIsPostLoadingInProgress(false);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  const getTaxonomyTerms = async (currentPostType) => {
    try {
      const termList = await apiFetch({
        path: addQueryArgs(`/api/b3rg/related-posts/berg-terms`, {
          postType: currentPostType,
        }),
      });
      setDefaultPostTaxonomies(termList);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (selection === 'auto') {
      getRelatedPosts({
        numberOfPosts,
        orderBy,
        currentPostId,
        order,
        postTaxonomies,
        selection,
      });
    }
    //get taxonomies terms
    getTaxonomyTerms(currentPostType);
  }, []); // eslint-disable-line

  // set block classes and set to blockProps
  const relatedPostClasses = getBlockClassNames(blockClassNames);
  const classes = classnames({
    'has-focusable-icon': enableFocusableIcon,
    disable: !blockVisibility,
  });

  const blockProps = useBlockProps({
    className: `${relatedPostClasses} ${classes} ${DEFAULT_CSS_CLASS.RELATED_POSTS}`,
  });

  const renderAppender = selection === 'auto' ? false : '';

  const { children, ...innerBlocksProps } = useInnerBlocksProps(blockProps, {
    allowedBlocks: ALLOWED_BLOCKS,
    renderAppender,
  });

  const blockNotice = isPostLoadingInProgress
    ? __('Discovering related posts… Please wait.', 'b3rg')
    : __('Sorry, no related content here. Try another article?', 'b3rg');

  let renderContent = (
    <div {...innerBlocksProps} key={blockProps.id}>
      {enableFocusableIcon && (
        <FocusableIcon {...{ title, icon: relatedPostsIcon }} />
      )}
      {postsAvailable ? (
        children
      ) : (
        <Notice status="info" isDismissible={false}>
          {blockNotice}
        </Notice>
      )}
    </div>
  );

  renderContent = <div>{renderContent}</div>;

  if (defaultPostTaxonomies) {
    for (const taxonomy in defaultPostTaxonomies) {
      const { label, terms } = defaultPostTaxonomies[taxonomy];
      const termListOptions = [];
      for (const term in terms) {
        const { name: label, term_id: value, taxonomy: type } = terms[term];
        termListOptions.push({ label, value, type });
      }
      defaultPostTaxonomyOptions.push({
        label,
        options: termListOptions,
      });
    }
  }

  const generalTabProps = {
    ...props,
    defaultPostTaxonomyOptions,
    getRelatedPosts,
  };

  return (
    <>
      <InspectorTabs>
        <InspectorGeneral {...generalTabProps} />
        <InspectorAppearance {...props} />
      </InspectorTabs>
      {renderContent}
    </>
  );
};

export default Edit;
